// @ts-strict-ignore
import identity from 'lodash/identity';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import moment from 'moment';

import {SORT_ORDERING} from 'common/util/array';
import {Cache} from 'common/util/cache';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {currentProjectStore} from 'common/report/stores/project-details';
import {HeaderType} from 'common/report/util/sorting/types';
import {BaseTimeUnit, ExtendedTimeUnit, SortOrder} from 'common/types/reports/bookmark';
import {getOwnAttr} from 'common/util';
import {PropertyType} from 'common/report/constants';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';

export {HeaderType} from 'common/report/util/sorting/types';

// "123456"
// "3 times"
export function parseNumber(val: string | number): number {
  if (Number.isFinite(val)) {
    return val as number;
  } else if (!val) {
    return null;
  }
  const num = Number(isString(val) ? val.replace(/ times?$/, ``).replace(/ users?$/, ``) : val);
  return Number.isFinite(num) ? num : null;
}

function parseScientificNumber(numStr: string): number {
  const val = Number(numStr);
  if (!Number.isNaN(val)) {
    return val;
  }
  const unit = numStr.charAt(numStr.length - 1);
  const magnitude = Number(numStr.substring(0, numStr.length - 1));
  const multiplier = {
    K: 10 ** 3,
    M: 10 ** 6,
    B: 10 ** 9,
    T: 10 ** 12,
    P: 10 ** 15,
  }[unit];
  return multiplier ? magnitude * multiplier : null;
}

const NUMBER_REGEX = `-?\\d+\\.?\\d*[K|M|B|T|P]{0,1}`;
const DATE_UNIT_REGEX = `(?:[Dd]ays?|[Ww]eeks?|[Hh]ours?|[Mm]onths?|[Qq]uarters?|[Yy]ears?|[Mm]inutes?|[Ss]econds?)`;

/** matches < 3 times, < 3 Days, < 3 */
const LESS_THAN_REGEXP = new RegExp(`^< (${NUMBER_REGEX})(?: times?| users?| ${DATE_UNIT_REGEX})?$`);

/** matches >= 3 times, >= 3 Days, >= 3 */
const GREATER_THAN_REGEXP = new RegExp(`^>= (${NUMBER_REGEX})(?: times?| users?| ${DATE_UNIT_REGEX})?$`);

/** matches 1 - 2 times, 1 time, 2 times, 1 - 2 Days, 1 Day, 2 Days */
const RANGE_REGEXP = new RegExp(`^(${NUMBER_REGEX})(?: - (${NUMBER_REGEX}))?(?: times?| users?| ${DATE_UNIT_REGEX})?$`);

export type NumberRange = [number, number] | [null, number] | [number, null];

export function parseNumberRange(val: string): NumberRange | null {
  val = val.replace(/,/g, ``);

  const lowerMatch = LESS_THAN_REGEXP.exec(val);
  if (lowerMatch) {
    return [null, parseScientificNumber(lowerMatch[1])];
  }

  const higherMatch = GREATER_THAN_REGEXP.exec(val);
  if (higherMatch) {
    return [parseScientificNumber(higherMatch[1]), null];
  }

  // matches 1 - 2 times, 1 time, 2 times
  const rangeMatches = RANGE_REGEXP.exec(val);
  if (rangeMatches) {
    return [parseScientificNumber(rangeMatches[1]), rangeMatches[2] ? parseScientificNumber(rangeMatches[2]) : null];
  }

  return null;
}

export function parseNumberRangeSortKey(
  val: string,
  {sortOrder = 1, ignoreNonNumeric = false}: {sortOrder?: number; ignoreNonNumeric?: boolean} = {},
): number | null {
  const range = parseNumberRange(val);
  if (!range) {
    if (ignoreNonNumeric) {
      return null;
    }

    // Ensures non-numeric values (e.g. "undefined") are at the end regardless of sort order
    return sortOrder < 0 ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
  } else if (!Number.isFinite(range[0]) && Number.isFinite(range[1])) {
    // For "< [number]", return `number - 1`
    return range[1] - 1;
  }
  return range[0];
}

export function parseDateUnitFromRange(val: string): TimeUnit {
  if (val.match(DATE_UNIT_REGEX)) {
    let dateUnit = val.match(DATE_UNIT_REGEX)[0];
    // normalize the string to lowercase and singular form
    if (dateUnit.endsWith(`s`)) {
      dateUnit = dateUnit.slice(0, -1);
    }

    if (dateUnit in BaseTimeUnit) {
      return BaseTimeUnit[dateUnit];
    } else if (dateUnit in ExtendedTimeUnit) {
      return ExtendedTimeUnit[dateUnit];
    }
    throw new Error(`Unrecognized TimeUnit: ${dateUnit}`);
  }
  return null;
}

function toDate(momentObj: moment.Moment): Date | null {
  return momentObj.isValid() ? momentObj.toDate() : null;
}

// The following date parser are based on the date formats defined at
// https://github.com/mixpanel/analytics/blob/0dc1062824f5ad823634bb4456509414865b09d8/api/version_2_0/utils/segment_utils.py#L192

// "2018-07-02T13:00:00-07:00"
export function parseDateIso(val: string): Date | null {
  return toDate(moment.utc(val, moment.ISO_8601, true));
}

// NOTE: This format is what's output from (new Date()).toString()
// "Fri Apr 06 2018 00:08:28 GMT+0000 (UTC)"
export function parseDateTypecastString(val: string): Date | null {
  return toDate(moment.utc(val, `ddd MMM DD YYYY HH:mm:ss [GMT+0000 (UTC)]`, true));
}

// "Fri Apr 6, 2020 9:00 PM"
export function parseDateHourly(val: string): Date | null {
  return toDate(moment.utc(val, `ddd MMM D, YYYY h:mm A`, true));
}

// "Jul 2, 2018"
export function parseDateDaily(val: string): Date | null {
  return toDate(moment.utc(val, `MMM D, YYYY`, true));
}

// "Apr 2, 2018 - Apr 8, 2018"
export function parseDateWeekly(val: string): Date | null {
  const dateStrs = val.split(` - `);
  if (dateStrs.length !== 2) {
    return null;
  }
  const dates = [
    toDate(moment.utc(dateStrs[0], `MMM D, YYYY`, true)),
    toDate(moment.utc(dateStrs[1], `MMM D, YYYY`, true)),
  ];
  return dates.every((date) => !!date) ? dates[1] : null;
}

// "Apr 2018"
export function parseDateMonthly(val: string): Date | null {
  return toDate(moment.utc(val, `MMM YYYY`, true));
}

// "Q1 2018"
export function parseDateQuarterly(val: string): Date | null {
  return toDate(moment.utc(val, `[Q]Q YYYY`, true));
}

// "Friday > Thursday > Wednesday..."
export const DAY_OF_WEEK_SORTING_PRIORITIES = {
  MONDAY: 0,
  TUESDAY: 1,
  WEDNESDAY: 2,
  THURSDAY: 3,
  FRIDAY: 4,
  SATURDAY: 5,
  SUNDAY: 6,
};

export function parseDateDayOfWeek(val: string): number | null {
  const startOfWeek = currentProjectStore.value?.timePeriodSettings.weekStartDay ?? 0;
  const priority = getOwnAttr(DAY_OF_WEEK_SORTING_PRIORITIES, val.toUpperCase());
  if (!Number.isInteger(priority)) {
    return null;
  }

  const offset = priority - startOfWeek;
  return offset < 0 ? 7 + offset : offset;
}

export function parseString(val: string): string {
  return `${val}`.toLowerCase();
}

export type Sortable = Date | number | string | null;
export type Parser = (val: string) => Sortable;

const HEADER_TYPE_TO_PARSER: {[key in HeaderType]: Parser} = {
  [HeaderType.DATE_ISO]: parseDateIso,
  [HeaderType.DATE_TYPECAST_STRING]: parseDateTypecastString,
  [HeaderType.DATE_HOURLY]: parseDateHourly,
  [HeaderType.DATE_DAILY]: parseDateDaily,
  [HeaderType.DATE_WEEKLY]: parseDateWeekly,
  [HeaderType.DATE_MONTHLY]: parseDateMonthly,
  [HeaderType.DATE_QUARTERLY]: parseDateQuarterly,
  [HeaderType.DATE_DAY_OF_WEEK]: parseDateDayOfWeek,
  [HeaderType.NUMBER]: parseNumber,
  [HeaderType.NUMBER_RANGE]: (val) => parseNumberRangeSortKey(val, {ignoreNonNumeric: true}),
  [HeaderType.STRING]: parseString,
};

const DATE_HEADER_TYPES = [
  HeaderType.DATE_ISO,
  HeaderType.DATE_TYPECAST_STRING,
  HeaderType.DATE_DAY_OF_WEEK,
  HeaderType.DATE_HOURLY,
  HeaderType.DATE_DAILY,
  HeaderType.DATE_WEEKLY,
  HeaderType.DATE_MONTHLY,
  HeaderType.DATE_QUARTERLY,
];

const HEADER_TYPE_PARSE_ORDER = [HeaderType.NUMBER, HeaderType.NUMBER_RANGE, ...DATE_HEADER_TYPES, HeaderType.STRING];

const HEADER_TYPE_PARSE_CACHES = {};

export function parseHeaderType({headerType, value}: {headerType: HeaderType; value: string}): Sortable {
  if (!Object.prototype.hasOwnProperty.call(HEADER_TYPE_PARSE_CACHES, headerType)) {
    HEADER_TYPE_PARSE_CACHES[headerType] = new Cache();
  }
  const cache = HEADER_TYPE_PARSE_CACHES[headerType];
  return cache.fetch(value, () => {
    return HEADER_TYPE_TO_PARSER[headerType](value);
  });
}

export function getHeaderType(value: string): HeaderType {
  const SPECIAL_VALUES = [`undefined`, OTHER_SEGMENT_VALUE];
  if (SPECIAL_VALUES.includes(value)) {
    return null;
  }

  return HEADER_TYPE_PARSE_ORDER.find((headerType) => {
    return !isNil(parseHeaderType({headerType, value}));
  });
}

export function getPropertyTypeFromHeaderType(headerType: HeaderType): PropertyType {
  if (DATE_HEADER_TYPES.includes(headerType)) {
    return PropertyType.Datetime;
  } else if ([HeaderType.NUMBER, HeaderType.NUMBER_RANGE].includes(headerType)) {
    return PropertyType.Number;
  }
  return PropertyType.String;
}

// we expect a type with the `label` property as a special case to make sure the "Other" segment is last
export type LabeledSegment<T> = {label?: string} & T;
export type TypedComparator<T> = (segment1: LabeledSegment<T>, segment2: LabeledSegment<T>) => number;

export function typedComparator<T>({
  sortType,
  order,
  transform = identity,
}: {
  sortType: HeaderType;
  order: SortOrder;
  transform: (arg: LabeledSegment<T>) => string;
}): TypedComparator<T> {
  const isDateHeader = DATE_HEADER_TYPES.includes(sortType);

  if (isDateHeader) {
    // This function is used for Insights reports. For legacy reason, the sort order for date properties is inverted,
    // so we gotta invert it again to obtain the actual ordering.
    order = order === SortOrder.Ascending ? SortOrder.Descending : SortOrder.Ascending;
  }

  return (segment1: LabeledSegment<T>, segment2: LabeledSegment<T>) => {
    if (segment1.label === OTHER_SEGMENT_VALUE) {
      return 1;
    } else if (segment2.label === OTHER_SEGMENT_VALUE) {
      return -1;
    }

    const args: [Sortable, Sortable] = [
      parseHeaderType({headerType: sortType, value: transform(segment1)}),
      parseHeaderType({headerType: sortType, value: transform(segment2)}),
    ];

    /**
     * Sorting comparisons are performed with less or equal than (<=), dates need to be compared
     * with >= to generate the correct ordering (Jan 5 '20 comes first than Jan 10 '20)
     * to avoid creating a new comparator, we switch the order of comparisons
     */
    return SORT_ORDERING[order].apply(null, isDateHeader ? [args[1], args[0]] : args);
  };
}

export function parseDateSortKey(value: string): Date | null {
  for (const headerType of DATE_HEADER_TYPES) {
    const parsedValue = parseHeaderType({headerType, value});
    if (parsedValue) {
      return parsedValue as Date;
    }
  }
  return null;
}
